/**
 * NS solver-state serialization for the off-main-thread Web Worker boundary.
 *
 * PHASE 1 — NOT WIRED INTO ANY RUNTIME PATH. This module only defines a
 * lossless serialize/deserialize round-trip for `NSSolverState`, so that the
 * full solver state can cross a `postMessage` (structured clone) or
 * `JSON.stringify` boundary to a worker and back without changing a single
 * numeric the SIMPLE solver reads or writes.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * WHY A HAND-WRITTEN CODEC (and not raw structured clone)?
 * ──────────────────────────────────────────────────────────────────────────
 * `NSSolverState` is NOT a plain data tree. It contains:
 *
 *   1. `Map<string, …>` for pipes / junctions / components. structuredClone
 *      preserves Maps, but `JSON.stringify` does NOT (a Map serializes to
 *      `{}`). We target BOTH transports, so Maps are encoded as an ORDERED
 *      array of `[key, value]` entries. Insertion order is preserved exactly,
 *      because the solver iterates `pipes.values()` / `junctions.values()` and
 *      order-sensitive reductions (mass-weighted junction P/h, adaptive
 *      relaxation history) must replay identically.
 *
 *   2. COMPONENT CLASS INSTANCES (NSPump, NSTurbine, …). Neither structuredClone
 *      nor JSON can carry a class instance: methods (`applyBoundaryConditions`)
 *      and the prototype are lost, and structuredClone THROWS on a function.
 *      We therefore encode each component as `{ type, id, params, privateState }`
 *      and REBUILD the correct class via a `type → constructor` registry on the
 *      far side. `params` is captured wholesale (it is a `Record<string, any>`
 *      of JSON-safe values in every shipped network), INCLUDING the underscore-
 *      prefixed fields the solver writes back mid-solve (`_inletRho`,
 *      `_currentFlow`, `_etaV`, `_actualSpeed`, `_lastMetadata`, `_baseConfig`'s
 *      siblings, HX `_C_hot`/`_T_hot_in`, …) and the nested `_lastMetadata`
 *      object. Crucially, several components also carry PRIVATE instance fields
 *      that live OUTSIDE `params` and are mutated across steps:
 *        • NSPump.actualSpeed              (rad/s, dynamic-speed integrator)
 *        • NSReliefValve.isOpen            (hysteresis latch)
 *        • NSPressureRegulator.integral    (PID integral accumulator)
 *        • NSPressureRegulator.prevError   (PID derivative memory)
 *      A naive `{...component}` would DROP these and silently reset the
 *      integrators — exactly the "stale state" class of bug CLAUDE.md warns
 *      about. We capture and restore them explicitly via `privateState`.
 *
 *   3. The `_baseConfig` shadow field stashed on the state object by the live
 *      step adapter (`stepAdapter.ts`). It is not part of the `NSSolverState`
 *      type but is read every step, so we round-trip it when present.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * FIELDS THAT CANNOT CROSS A STRUCTURED-CLONE / JSON BOUNDARY
 * ──────────────────────────────────────────────────────────────────────────
 *   • Component methods / prototypes — reconstructed from `type`, NOT copied.
 *   • Legacy `NSCustom.params.logic` is an inert string (clonable). NSCustom
 *     preserves it for exact reload/worker compatibility but always rejects
 *     execution. No function is compiled on either side of the boundary.
 *   • `NaN` / `±Infinity`: survive `structuredClone` natively. Under JSON they
 *     would become `null`, so `numberToJSON` / `numberFromJSON` encode them as
 *     tagged sentinel strings, making the JSON path lossless for the residual
 *     `Infinity` seeds, `StatePoint` NaN defaults, and pump NPSH `NaN`s.
 *   • `-0`: preserved (encoded as the `"-0"` sentinel) so bit-identity holds.
 *
 * Everything the SIMPLE solver reads or writes is covered; the parity test in
 * `__tests__/serializationParity.test.ts` is the executable proof.
 */

import type {
  NSSolverState,
  PipeSegment,
  Junction,
  JunctionConnection,
  CellState,
  FaceState,
  NSComponent,
  SIMPLEConfig,
  Phase,
} from '../types';
import { createNSSolverState } from '../network/nsNetwork';

// Component classes — imported individually (the codebase has no barrel; we
// mirror networkBuilder.ts so the registry covers every shipped component).
import { NSBoundary } from '../components/nsBoundary';
import { NSPump } from '../components/nsPump';
import { NSTurbine } from '../components/nsTurbine';
import { NSBoiler } from '../components/nsBoiler';
import { NSCondenser } from '../components/nsCondenser';
import { NSValve } from '../components/nsValve';
import { NSHeatExchanger } from '../components/nsHeatExchanger';
import { NSSplitter } from '../components/nsSplitter';
import { NSMixer } from '../components/nsMixer';
import { NSEjector } from '../components/nsEjector';
import { NSCompressor } from '../components/nsCompressor';
import { NSCustom } from '../components/nsCustom';
import { NSCheckValve } from '../components/nsCheckValve';
import { NSReliefValve } from '../components/nsReliefValve';
import { NSControlValve } from '../components/nsControlValve';
import { NSPressureRegulator } from '../components/nsPressureRegulator';

// ──────────────────────────────────────────────────────────────────────────
// JSON-safe number encoding (keeps the JSON transport lossless)
// ──────────────────────────────────────────────────────────────────────────

/**
 * A number, or a tagged sentinel for the values plain JSON cannot represent.
 * structuredClone does not need these (it carries NaN/Infinity/-0 natively),
 * but `JSON.stringify` collapses NaN/Infinity to `null` and loses `-0`, so we
 * tag them. Decoding accepts both tagged strings and raw numbers, so a state
 * that crossed via structuredClone (raw numbers) decodes identically.
 */
export type JSONNumber = number | 'NaN' | 'Infinity' | '-Infinity' | '-0';

export function numberToJSON(x: number): JSONNumber {
  if (Number.isNaN(x)) return 'NaN';
  if (x === Infinity) return 'Infinity';
  if (x === -Infinity) return '-Infinity';
  if (x === 0 && 1 / x === -Infinity) return '-0';
  return x;
}

export function numberFromJSON(x: JSONNumber): number {
  if (typeof x === 'number') return x;
  switch (x) {
    case 'NaN':
      return NaN;
    case 'Infinity':
      return Infinity;
    case '-Infinity':
      return -Infinity;
    case '-0':
      return -0;
    default:
      // Defensive: an unrecognized string is non-finite garbage → NaN.
      return NaN;
  }
}

/**
 * Deep-encode an arbitrary JSON-ish value, tagging every number so the JSON
 * transport is lossless. Used for the open-ended component `params` bag, whose
 * values are numbers, strings, booleans, nested plain objects (e.g.
 * `_lastMetadata`, custom `properties`), and arrays.
 *
 * NOTE: functions are NOT expected here and are dropped (returned as
 * `undefined`) — see the module header. No shipped component stores a function
 * in `params`.
 */
function encodeValue(v: unknown): unknown {
  if (typeof v === 'number') return numberToJSON(v);
  if (v === null || v === undefined) return v;
  if (typeof v === 'function') return undefined; // cannot cross boundary
  if (Array.isArray(v)) return v.map(encodeValue);
  if (typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v as Record<string, unknown>)) {
      out[k] = encodeValue((v as Record<string, unknown>)[k]);
    }
    return out;
  }
  return v; // string | boolean
}

function decodeValue(v: unknown): unknown {
  if (typeof v === 'number') return v;
  if (
    v === 'NaN' ||
    v === 'Infinity' ||
    v === '-Infinity' ||
    v === '-0'
  ) {
    return numberFromJSON(v as JSONNumber);
  }
  if (v === null || v === undefined) return v;
  if (Array.isArray(v)) return v.map(decodeValue);
  if (typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v as Record<string, unknown>)) {
      out[k] = decodeValue((v as Record<string, unknown>)[k]);
    }
    return out;
  }
  return v;
}

// ──────────────────────────────────────────────────────────────────────────
// Serialized shapes (plain, JSON-safe, structured-clone-safe)
// ──────────────────────────────────────────────────────────────────────────

export interface SerializedCellState {
  P: JSONNumber;
  h: JSONNumber;
  rho: JSONNumber;
  T: JSONNumber;
  u_int: JSONNumber;
  x: JSONNumber;
  c: JSONNumber;
  mu: JSONNumber;
  cp: JSONNumber;
  phase: Phase;
}

export interface SerializedFaceState {
  mdot: JSONNumber;
  u: JSONNumber;
}

export interface SerializedPipeSegment {
  id: string;
  diameter: JSONNumber;
  length: JSONNumber;
  roughness: JSONNumber;
  inclination: JSONNumber;
  wallHeatFlux: JSONNumber;
  nCells: number;
  dx: JSONNumber;
  area: JSONNumber;
  wallThickness?: JSONNumber;
  wallRhoCp?: JSONNumber;
  cells: SerializedCellState[];
  faces: SerializedFaceState[];
  cells_old: SerializedCellState[];
  faces_old: SerializedFaceState[];
  T_wall?: JSONNumber[];
  T_wall_old?: JSONNumber[];
}

export interface SerializedJunction {
  id: string;
  type: Junction['type'];
  connections: JunctionConnection[];
  P: JSONNumber;
  h: JSONNumber;
  componentId?: string;
}

export interface SerializedComponent {
  /** Discriminant used to rebuild the right class on the far side. */
  type: string;
  id: string;
  /** Full params bag (incl. solver-written `_`-fields), numbers tagged. */
  params: Record<string, unknown>;
  /** Private instance fields that live OUTSIDE `params` (see module header). */
  privateState: Record<string, JSONNumber | boolean>;
}

export interface SerializedSIMPLEConfig {
  maxOuterIterations: number;
  convergenceTolerance: JSONNumber;
  pressureRelaxation: JSONNumber;
  velocityRelaxation: JSONNumber;
  enthalpyRelaxation: JSONNumber;
  enableAcoustic: boolean;
}

export interface SerializedSolverState {
  /** Format version — lets a worker reject an incompatible payload. */
  version: 1;
  pipes: Array<[string, SerializedPipeSegment]>;
  junctions: Array<[string, SerializedJunction]>;
  components: Array<[string, SerializedComponent]>;
  config: SerializedSIMPLEConfig;
  time: JSONNumber;
  iteration: number;
  converged: boolean;
  residuals: {
    mass: JSONNumber;
    momentum: JSONNumber;
    energy: JSONNumber;
  };
  energyBalance?: {
    totalInternalEnergy: JSONNumber;
    totalKineticEnergy: JSONNumber;
    imbalance: JSONNumber;
  };
  /** Shadow base-config stashed on the live state by stepAdapter.ts. */
  baseConfig?: SerializedSIMPLEConfig;
}

// ──────────────────────────────────────────────────────────────────────────
// Component reconstruction registry
// ──────────────────────────────────────────────────────────────────────────

type CtorFromParams = (id: string, params: Record<string, any>) => NSComponent;

/**
 * `type` string (the component's `type` field) → factory that rebuilds the
 * class from its (already-deserialized) params. Every constructor accepts
 * `(id, params)`; the params bag already carries the component's full
 * configuration, so re-running the constructor with it reproduces the instance.
 */
const COMPONENT_REGISTRY: Record<string, CtorFromParams> = {
  boundary: (id, p) => new NSBoundary(id, p),
  pump: (id, p) => new NSPump(id, p),
  turbine: (id, p) => new NSTurbine(id, p),
  boiler: (id, p) => new NSBoiler(id, p),
  condenser: (id, p) => new NSCondenser(id, p),
  valve: (id, p) => new NSValve(id, p),
  heatExchanger: (id, p) => new NSHeatExchanger(id, p),
  splitter: (id, p) => new NSSplitter(id, p),
  mixer: (id) => new NSMixer(id),
  ejector: (id, p) => new NSEjector(id, p),
  compressor: (id, p) => new NSCompressor(id, p),
  custom: (id, p) => new NSCustom(id, p),
  checkValve: (id, p) => new NSCheckValve(id, p),
  reliefValve: (id, p) => new NSReliefValve(id, p),
  controlValve: (id, p) => new NSControlValve(id, p),
  pressureRegulator: (id, p) => new NSPressureRegulator(id, p),
};

/**
 * Private (non-`params`) instance fields, per component type, that must be
 * captured/restored to survive the boundary. Keyed by `type`; each entry lists
 * the instance property names. The values are numbers or booleans.
 */
const PRIVATE_FIELDS: Record<string, readonly string[]> = {
  pump: ['actualSpeed'],
  reliefValve: ['isOpen'],
  pressureRegulator: ['integral', 'prevError'],
  // N2: hysteretic disc latch (open/closed memory across steps). Skipped when
  // still undefined (not yet initialised) — the rebuilt instance re-derives it
  // from the cracking-ΔP criterion, matching a fresh valve.
  checkValve: ['isOpenLatch'],
};

// ──────────────────────────────────────────────────────────────────────────
// Cell / face / pipe / junction codecs
// ──────────────────────────────────────────────────────────────────────────

function serializeCell(c: CellState): SerializedCellState {
  return {
    P: numberToJSON(c.P),
    h: numberToJSON(c.h),
    rho: numberToJSON(c.rho),
    T: numberToJSON(c.T),
    u_int: numberToJSON(c.u_int),
    x: numberToJSON(c.x),
    c: numberToJSON(c.c),
    mu: numberToJSON(c.mu),
    cp: numberToJSON(c.cp),
    phase: c.phase,
  };
}

function deserializeCell(s: SerializedCellState): CellState {
  return {
    P: numberFromJSON(s.P),
    h: numberFromJSON(s.h),
    rho: numberFromJSON(s.rho),
    T: numberFromJSON(s.T),
    u_int: numberFromJSON(s.u_int),
    x: numberFromJSON(s.x),
    c: numberFromJSON(s.c),
    mu: numberFromJSON(s.mu),
    cp: numberFromJSON(s.cp),
    phase: s.phase,
  };
}

function serializeFace(f: FaceState): SerializedFaceState {
  return { mdot: numberToJSON(f.mdot), u: numberToJSON(f.u) };
}

function deserializeFace(s: SerializedFaceState): FaceState {
  return { mdot: numberFromJSON(s.mdot), u: numberFromJSON(s.u) };
}

function serializePipe(p: PipeSegment): SerializedPipeSegment {
  const out: SerializedPipeSegment = {
    id: p.id,
    diameter: numberToJSON(p.diameter),
    length: numberToJSON(p.length),
    roughness: numberToJSON(p.roughness),
    inclination: numberToJSON(p.inclination),
    wallHeatFlux: numberToJSON(p.wallHeatFlux),
    nCells: p.nCells,
    dx: numberToJSON(p.dx),
    area: numberToJSON(p.area),
    cells: p.cells.map(serializeCell),
    faces: p.faces.map(serializeFace),
    cells_old: p.cells_old.map(serializeCell),
    faces_old: p.faces_old.map(serializeFace),
  };
  if (p.wallThickness !== undefined) out.wallThickness = numberToJSON(p.wallThickness);
  if (p.wallRhoCp !== undefined) out.wallRhoCp = numberToJSON(p.wallRhoCp);
  if (p.T_wall !== undefined) out.T_wall = p.T_wall.map(numberToJSON);
  if (p.T_wall_old !== undefined) out.T_wall_old = p.T_wall_old.map(numberToJSON);
  return out;
}

function deserializePipe(s: SerializedPipeSegment): PipeSegment {
  const out: PipeSegment = {
    id: s.id,
    diameter: numberFromJSON(s.diameter),
    length: numberFromJSON(s.length),
    roughness: numberFromJSON(s.roughness),
    inclination: numberFromJSON(s.inclination),
    wallHeatFlux: numberFromJSON(s.wallHeatFlux),
    nCells: s.nCells,
    dx: numberFromJSON(s.dx),
    area: numberFromJSON(s.area),
    cells: s.cells.map(deserializeCell),
    faces: s.faces.map(deserializeFace),
    cells_old: s.cells_old.map(deserializeCell),
    faces_old: s.faces_old.map(deserializeFace),
  };
  if (s.wallThickness !== undefined) out.wallThickness = numberFromJSON(s.wallThickness);
  if (s.wallRhoCp !== undefined) out.wallRhoCp = numberFromJSON(s.wallRhoCp);
  if (s.T_wall !== undefined) out.T_wall = s.T_wall.map(numberFromJSON);
  if (s.T_wall_old !== undefined) out.T_wall_old = s.T_wall_old.map(numberFromJSON);
  return out;
}

function serializeJunction(j: Junction): SerializedJunction {
  const out: SerializedJunction = {
    id: j.id,
    type: j.type,
    // Connections are plain data (pipeId/faceIndex/direction). Clone the array
    // and each entry so the deserialized state shares no references with the
    // original — a worker boundary would not share references either.
    connections: j.connections.map((c) => ({
      pipeId: c.pipeId,
      faceIndex: c.faceIndex,
      direction: c.direction,
    })),
    P: numberToJSON(j.P),
    h: numberToJSON(j.h),
  };
  if (j.componentId !== undefined) out.componentId = j.componentId;
  return out;
}

function deserializeJunction(s: SerializedJunction): Junction {
  const out: Junction = {
    id: s.id,
    type: s.type,
    connections: s.connections.map((c) => ({
      pipeId: c.pipeId,
      faceIndex: c.faceIndex,
      direction: c.direction,
    })),
    P: numberFromJSON(s.P),
    h: numberFromJSON(s.h),
  };
  if (s.componentId !== undefined) out.componentId = s.componentId;
  return out;
}

function serializeComponent(c: NSComponent): SerializedComponent {
  const privateState: Record<string, JSONNumber | boolean> = {};
  const fields = PRIVATE_FIELDS[c.type] ?? [];
  for (const field of fields) {
    const v = (c as unknown as Record<string, unknown>)[field];
    if (typeof v === 'boolean') {
      privateState[field] = v;
    } else if (typeof v === 'number') {
      privateState[field] = numberToJSON(v);
    }
    // Missing/other-typed private fields are skipped; the constructor default
    // then applies on rebuild (matches a fresh instance).
  }

  return {
    type: c.type,
    id: c.id,
    params: encodeValue(c.params) as Record<string, unknown>,
    privateState,
  };
}

function deserializeComponent(s: SerializedComponent): NSComponent {
  const factory = COMPONENT_REGISTRY[s.type];
  if (!factory) {
    throw new Error(
      `deserializeSolverState: unknown component type "${s.type}" (id="${s.id}"). ` +
        `Add it to COMPONENT_REGISTRY in worker/serialization.ts.`,
    );
  }

  const params = decodeValue(s.params) as Record<string, any>;
  const comp = factory(s.id, params);

  // The constructor merges its own defaults over the passed params. To stay
  // LOSSLESS we overwrite `comp.params` with the exact deserialized bag (it
  // already contains every default the original instance carried, plus all
  // solver-written `_`-fields). This guarantees byte-identical params.
  comp.params = params;

  // Restore private (non-params) instance fields.
  for (const field of Object.keys(s.privateState)) {
    (comp as unknown as Record<string, unknown>)[field] = numericOrBool(
      s.privateState[field],
    );
  }

  return comp;
}

function numericOrBool(v: JSONNumber | boolean): number | boolean {
  if (typeof v === 'boolean') return v;
  return numberFromJSON(v);
}

function serializeConfig(c: SIMPLEConfig): SerializedSIMPLEConfig {
  return {
    maxOuterIterations: c.maxOuterIterations,
    convergenceTolerance: numberToJSON(c.convergenceTolerance),
    pressureRelaxation: numberToJSON(c.pressureRelaxation),
    velocityRelaxation: numberToJSON(c.velocityRelaxation),
    enthalpyRelaxation: numberToJSON(c.enthalpyRelaxation),
    enableAcoustic: c.enableAcoustic,
  };
}

function deserializeConfig(s: SerializedSIMPLEConfig): SIMPLEConfig {
  return {
    maxOuterIterations: s.maxOuterIterations,
    convergenceTolerance: numberFromJSON(s.convergenceTolerance),
    pressureRelaxation: numberFromJSON(s.pressureRelaxation),
    velocityRelaxation: numberFromJSON(s.velocityRelaxation),
    enthalpyRelaxation: numberFromJSON(s.enthalpyRelaxation),
    enableAcoustic: s.enableAcoustic,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Top-level codec
// ──────────────────────────────────────────────────────────────────────────

/**
 * Serialize a live `NSSolverState` into a plain, JSON-safe, structured-clone-
 * safe object. The round-trip through `deserializeSolverState` is LOSSLESS for
 * every value the SIMPLE solver reads or writes — see `serialization.test`
 * and `serializationParity.test`.
 */
export function serializeSolverState(state: NSSolverState): SerializedSolverState {
  const out: SerializedSolverState = {
    version: 1,
    pipes: Array.from(state.pipes.entries()).map(([k, p]) => [k, serializePipe(p)]),
    junctions: Array.from(state.junctions.entries()).map(([k, j]) => [
      k,
      serializeJunction(j),
    ]),
    components: Array.from(state.components.entries()).map(([k, c]) => [
      k,
      serializeComponent(c),
    ]),
    config: serializeConfig(state.config),
    time: numberToJSON(state.time),
    iteration: state.iteration,
    converged: state.converged,
    residuals: {
      mass: numberToJSON(state.residuals.mass),
      momentum: numberToJSON(state.residuals.momentum),
      energy: numberToJSON(state.residuals.energy),
    },
  };

  if (state.energyBalance) {
    out.energyBalance = {
      totalInternalEnergy: numberToJSON(state.energyBalance.totalInternalEnergy),
      totalKineticEnergy: numberToJSON(state.energyBalance.totalKineticEnergy),
      imbalance: numberToJSON(state.energyBalance.imbalance),
    };
  }

  // Shadow base-config from stepAdapter.ts (`(state as any)._baseConfig`).
  const baseConfig = (state as unknown as { _baseConfig?: SIMPLEConfig })._baseConfig;
  if (baseConfig) {
    out.baseConfig = serializeConfig(baseConfig);
  }

  return out;
}

/**
 * Rebuild a live `NSSolverState` (with Maps and reconstructed component class
 * instances) from a serialized payload. Insertion order of every Map is
 * preserved from the serialized entry arrays.
 */
export function deserializeSolverState(s: SerializedSolverState): NSSolverState {
  if (s.version !== 1) {
    throw new Error(
      `deserializeSolverState: unsupported payload version ${s.version} (expected 1).`,
    );
  }

  const pipes = new Map<string, PipeSegment>();
  for (const [k, p] of s.pipes) pipes.set(k, deserializePipe(p));

  const junctions = new Map<string, Junction>();
  for (const [k, j] of s.junctions) junctions.set(k, deserializeJunction(j));

  const components = new Map<string, NSComponent>();
  for (const [k, c] of s.components) components.set(k, deserializeComponent(c));

  const state = createNSSolverState(
    pipes,
    junctions,
    components,
    deserializeConfig(s.config),
  );

  state.time = numberFromJSON(s.time);
  state.iteration = s.iteration;
  state.converged = s.converged;
  state.residuals = {
    mass: numberFromJSON(s.residuals.mass),
    momentum: numberFromJSON(s.residuals.momentum),
    energy: numberFromJSON(s.residuals.energy),
  };

  if (s.energyBalance) {
    state.energyBalance = {
      totalInternalEnergy: numberFromJSON(s.energyBalance.totalInternalEnergy),
      totalKineticEnergy: numberFromJSON(s.energyBalance.totalKineticEnergy),
      imbalance: numberFromJSON(s.energyBalance.imbalance),
    };
  }

  if (s.baseConfig) {
    (state as unknown as { _baseConfig?: SIMPLEConfig })._baseConfig = deserializeConfig(
      s.baseConfig,
    );
  }

  return state;
}

/**
 * Convenience: round-trip through a JSON string (the strictest transport — it
 * forces the NaN/Infinity/-0 sentinels into play). Returns a fresh live state.
 * Useful for tests and for any worker that prefers a string payload.
 */
export function roundTripViaJSON(state: NSSolverState): NSSolverState {
  const json = JSON.stringify(serializeSolverState(state));
  const parsed = JSON.parse(json) as SerializedSolverState;
  return deserializeSolverState(parsed);
}
